import React, { useEffect, useRef, useState } from 'react';
import type { User } from 'firebase/auth';
import { Loader2, AlertCircle, X } from 'lucide-react';
import type { AuthChallenge, ChallengeOutcome } from '../../services/secureAuthShared';

interface SecureChallengeModalProps {
  pendingSessionId: string;
  challenge: AuthChallenge;
  resolveDevice: (pendingSessionId: string, code: string) => Promise<ChallengeOutcome>;
  resolveTotp: (pendingSessionId: string, code: string) => Promise<ChallengeOutcome>;
  resendDeviceCode: (pendingSessionId: string) => Promise<{ cooldownSeconds: number }>;
  onSuccess: (user: User) => void;
  onCancel: () => void;
}

const DIGIT_COUNT = 6;

/**
 * Pré-session : aucun useAuth()/user Firebase disponible ici (par design —
 * voir googleSecureAuthService.ts / passwordSecureAuthService.ts). Résout
 * séquentiellement le(s) challenge(s) requis (appareil non reconnu, puis/ou
 * TOTP) via pendingSessionId, jusqu'à obtenir un customToken et appeler
 * onSuccess. Rien n'est bypassable en fermant cette modale : il n'y a aucune
 * session à côté qu'elle protégerait, elle EST le seul chemin vers une
 * session — que ce soit après une connexion Google ou email/password (les
 * resolvers sont injectés par l'appelant, la logique d'affichage est
 * identique dans les deux cas).
 */
export function SecureChallengeModal({ pendingSessionId, challenge, resolveDevice, resolveTotp, resendDeviceCode, onSuccess, onCancel }: SecureChallengeModalProps) {
  const [step, setStep] = useState<'device' | 'totp'>(challenge.requiresDevice ? 'device' : 'totp');
  const [digits, setDigits] = useState<string[]>(Array(DIGIT_COUNT).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(challenge.cooldownSeconds ?? 0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const fullCode = digits.join('');

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown > 0]);

  const submitCode = async (codeToSubmit: string) => {
    if (codeToSubmit.trim().length < DIGIT_COUNT) return;
    setLoading(true);
    setError('');
    try {
      const resolve = step === 'device' ? resolveDevice : resolveTotp;
      const result = await resolve(pendingSessionId, codeToSubmit.trim());

      if (result.status === 'authenticated') {
        onSuccess(result.user);
        return;
      }

      // Un des deux sous-challenges vient d'être résolu, l'autre reste à faire.
      if (result.challenge.requiresDevice) {
        setStep('device');
      } else if (result.challenge.requiresTotp) {
        setStep('totp');
      }
      setDigits(Array(DIGIT_COUNT).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } catch (err: any) {
      setError(err.message || 'Code invalide');
      setDigits(Array(DIGIT_COUNT).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fullCode.length === DIGIT_COUNT && /^\d{6}$/.test(fullCode)) {
      submitCode(fullCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullCode]);

  const handleDigitChange = (index: number, value: string) => {
    const char = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = char;
    setDigits(newDigits);
    if (char && index < DIGIT_COUNT - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = '';
        setDigits(newDigits);
      } else if (index > 0) {
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        setDigits(newDigits);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < DIGIT_COUNT - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, DIGIT_COUNT);
    if (!pasted) return;
    const newDigits = Array(DIGIT_COUNT).fill('');
    pasted.split('').forEach((ch, i) => { newDigits[i] = ch; });
    setDigits(newDigits);
    const nextEmpty = Math.min(pasted.length, DIGIT_COUNT - 1);
    inputRefs.current[nextEmpty]?.focus();
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError('');
    try {
      const result = await resendDeviceCode(pendingSessionId);
      setCooldown(result.cooldownSeconds ?? 60);
      setDigits(Array(DIGIT_COUNT).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err.message || 'Erreur lors du renvoi du code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center sm:justify-center bg-black/40 sm:p-4 backdrop-blur-sm"
      style={{ marginTop: 0 }}
      onClick={onCancel}
    >
      <div
        className="w-full sm:mx-4 sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col"
        data-theme="light"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-1 sm:hidden flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="hidden sm:flex justify-end pt-4 pr-4 flex-shrink-0">
          <button
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-8 pb-8 pt-3 sm:pt-2">
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
              {step === 'device' ? 'Nouvel appareil détecté' : 'Vérification 2FA'}
            </h2>
            <p className="text-sm text-gray-400">
              {step === 'device'
                ? "On t'a envoyé un code à 6 chiffres par email pour confirmer que c'est bien toi."
                : "Ouvre ton app d'authentification pour obtenir le code."}
            </p>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Code à 6 chiffres
              </label>
              <div className="flex gap-2 justify-between" onPaste={handlePaste}>
                {digits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(i, e)}
                    autoFocus={i === 0}
                    disabled={loading}
                    className="w-10 sm:w-12 h-12 sm:h-14 rounded-xl text-center text-lg sm:text-xl font-bold text-gray-900 outline-none transition-all focus:ring-2 focus:ring-gray-900/20 focus:scale-105 caret-transparent select-none"
                    style={{
                      backgroundColor: digit ? '#f0f0f0' : '#f6f6f6',
                      border: digit ? '2px solid #d1d5db' : '2px solid transparent',
                    }}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 px-3 py-2.5 rounded-xl">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                disabled={loading || fullCode.length < DIGIT_COUNT}
                onClick={() => submitCode(fullCode)}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3.5 font-semibold text-white bg-gray-900 hover:bg-black transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                    <span className="text-white">Vérification...</span>
                  </>
                ) : (
                  <span className="text-white">Continuer</span>
                )}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="flex-none px-5 py-3.5 rounded-xl font-semibold text-gray-400 hover:text-gray-700 transition-colors"
              >
                Annuler
              </button>
            </div>

            {step === 'device' && (
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={cooldown > 0 || resending}
                  className="text-xs text-gray-400 hover:text-gray-700 transition-colors underline underline-offset-2 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  {resending && <Loader2 className="w-3 h-3 animate-spin" />}
                  {cooldown > 0 ? `Renvoyer dans ${cooldown}s` : 'Renvoyer le code'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SecureChallengeModal;
